import ViewList, { ViewListItem } from "@/components/layout/ViewList";
import BookInfoForm, { BookInfoFormValues } from "../BookInfoForm";
import { useViewList } from "@/context/ViewListContext";
import { FC, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { NavigateOptions } from "@vixi/router";
import { useTranslation } from "@vixi/localization";
import { Button, FileUploadBox, useGoBack, useCourseEditorContext } from "@vixi/ui";

export interface UploadEbookFormValues {
  ebook: File[];
  bookInfo: BookInfoFormValues;
}

interface UploadEbookProps {
  onSubmit?: (values: UploadEbookFormValues) => void;
  isSubmitting?: boolean;
}

export const UploadEbookStep: FC<UploadEbookProps> = ({ onSubmit, isSubmitting = false }) => {
  const viewList = useViewList();
  const { t, tFile, byLocale } = useTranslation();

  // Go back
  const goBack = useMemo(
    () =>
      viewList.activeStep === 0
        ? ({
            to: "/create/ebook",
            replace: true,
          } as NavigateOptions)
        : viewList.prevStep,
    [viewList.activeStep],
  );
  useGoBack(goBack);

  // Store
  const { setValue } = useCourseEditorContext();

  // Form
  const {
    control,
    watch,
    setValue: setFormValue,
    getValues: getFormValue,
  } = useForm<UploadEbookFormValues>();
  const showNextBtn = watch("ebook")?.length > 0;

  // Sync form values with store
  const syncFormValues = (values: UploadEbookFormValues) => {
    const options = { mustExist: false };

    setValue(["ebookAbout"], values.bookInfo.about, options);
    setValue(["targetAudience"], values.bookInfo.audience, options);
    setValue(["conversationTone"], values.bookInfo.tone, options);
  };

  return (
    <ViewList {...viewList}>
      {/* Pick pdf to upload */}
      <ViewListItem>
        <div className="mx-auto my-auto max-w-full">
          <Controller
            name="ebook"
            control={control}
            render={({ field }) => (
              <FileUploadBox
                multiple
                image={{
                  src: byLocale({
                    ar: "/images/ar-bubble-upload-pic.webp",
                    fallback: "/images/bubble-upload-pdf.webp",
                  }),
                  alt: "Fox Icon",
                }}
                imageSize={{ width: "507px", height: "470px" }}
                dropzoneLabel={t("Action.DropEbook")}
                imageMarginBlock={byLocale({ ar: "-155px 0", fallback: "-155px 0" })}
                accept="application/pdf"
                {...field}
              />
            )}
          />
        </div>

        {showNextBtn && (
          <div className="absolute end-20 bottom-10">
            <Button
              variant="contained"
              onClick={() => viewList?.nextStep()}
              size="large"
              className="px-12"
            >
              {t("Action.Next")}
            </Button>
          </div>
        )}
      </ViewListItem>

      {/* Book Info Form */}
      <ViewListItem>
        <BookInfoForm
          onSubmit={(bookInfoValues) => {
            // Create complete form values with bookInfo
            const completeValues: UploadEbookFormValues = {
              ...getFormValue(),
              bookInfo: bookInfoValues,
            };
            
            // Save to form state
            setFormValue("bookInfo", bookInfoValues);
            
            // Sync to store
            syncFormValues(completeValues);
            
            // Call onSubmit with complete values (including bookInfo)
            if (!!onSubmit && !isSubmitting) {
              console.log("📝 Submitting form with values:", {
                hasEbook: !!completeValues.ebook?.length,
                hasBookInfo: !!completeValues.bookInfo,
                about: completeValues.bookInfo?.about || "MISSING",
                audience: completeValues.bookInfo?.audience || "MISSING",
                tone: completeValues.bookInfo?.tone || "MISSING",
                completeValues,
              });
              onSubmit(completeValues);
            }
          }}
          isSubmitting={isSubmitting}
        />
      </ViewListItem>
    </ViewList>
  );
};
